import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Trophy, Star, Clock, MapPin, Tv, ChevronRight, 
  TrendingUp, Award, Calendar, Users, Flame
} from 'lucide-react';
import SWACWeeklyAwards from '../components/SWACWeeklyAwards';
import PlayersToWatch from '../components/PlayersToWatch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Game Card Component
const GameCard = ({ game, isLive = false }) => {
  const awayTeam = game.away_team || {};
  const homeTeam = game.home_team || {};
  
  return (
    <div 
      className={`bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border ${
        isLive ? 'border-green-500 ring-2 ring-green-500/50' : 'border-white/20'
      } hover:bg-white/15 transition-all`}
      data-testid={`game-card-${game.id}`}
    >
      {/* Game Status Bar */}
      <div className={`px-4 py-2 flex items-center justify-between ${
        isLive ? 'bg-green-600' : game.status === 'final' ? 'bg-slate-700' : 'bg-blue-900'
      }`}>
        <div className="flex items-center gap-2">
          {isLive && <span className="w-2 h-2 bg-white rounded-full animate-pulse" />}
          <span className="text-white text-sm font-bold uppercase">
            {isLive ? 'LIVE' : game.status === 'final' ? 'FINAL' : game.game_label || 'Scheduled'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-white/80 text-xs">
          <Tv className="w-3 h-3" />
          <span>{game.network || 'TBD'}</span>
        </div>
      </div>
      
      {/* Teams */}
      <div className="p-4">
        {/* Away Team */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <img 
              src={awayTeam.logo_url} 
              alt={awayTeam.name}
              className="w-10 h-10 object-contain"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
            />
            <span className="text-white font-bold">{awayTeam.name}</span>
          </div>
          <span className={`text-2xl font-bold ${
            game.away_score > game.home_score ? 'text-green-400' : 'text-white'
          }`}>
            {game.away_score ?? '-'}
          </span>
        </div>
        
        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={homeTeam.logo_url} 
              alt={homeTeam.name}
              className="w-10 h-10 object-contain"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
            />
            <span className="text-white font-bold">{homeTeam.name}</span>
          </div>
          <span className={`text-2xl font-bold ${
            game.home_score > game.away_score ? 'text-green-400' : 'text-white'
          }`}>
            {game.home_score ?? '-'}
          </span>
        </div>
        
        {/* Game Info */}
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-white/60">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{new Date(game.kickoff_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>{game.venue || 'TBD'}</span>
          </div>
        </div>
        
        {game.is_game_of_week && (
          <div className="mt-3 flex items-center gap-2 text-yellow-400 text-xs font-bold">
            <Star className="w-4 h-4 fill-yellow-400" />
            GAME OF THE WEEK
          </div>
        )}
      </div>
    </div>
  );
};

// Related Article Card
const RelatedArticleCard = ({ article }) => (
  <Link 
    to={article.redirect_url || `/article/${article.article_id}`}
    className="block bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden border border-white/20 hover:bg-white/15 transition-all group"
  >
    <div className="aspect-video overflow-hidden">
      <img 
        src={article.image_url} 
        alt={article.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => { e.target.src = 'https://via.placeholder.com/300x169?text=SheLovesThee'; }}
      />
    </div>
    <div className="p-3">
      <span className="text-blue-400 text-xs font-bold uppercase">{article.category}</span>
      <h4 className="text-white font-bold text-sm mt-1 line-clamp-2 group-hover:text-blue-400 transition-colors">
        {article.title}
      </h4>
    </div>
  </Link>
);

export default function GamedayScoreboardPage() {
  const [games, setGames] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(null);
  
  const fetchGames = useCallback(async () => {
    try {
      // Get current week
      const weeksRes = await fetch(`${BACKEND_URL}/api/pickem/weeks`);
      const weeks = await weeksRes.json();
      const openWeek = weeks.find(w => w.is_open) || weeks[weeks.length - 1];
      setCurrentWeek(openWeek);
      
      if (openWeek) {
        const gamesRes = await fetch(`${BACKEND_URL}/api/pickem/games?week_id=${openWeek.id}`);
        const gamesData = await gamesRes.json();
        setGames(gamesData);
      }
      
      // Get related articles
      const articlesRes = await fetch(`${BACKEND_URL}/api/articles`);
      const articlesData = await articlesRes.json();
      // Filter for football/SWAC related articles
      const filtered = (Array.isArray(articlesData) ? articlesData : articlesData.articles || [])
        .filter(a => ['SWAC', 'Football', 'Jackson State'].includes(a.category))
        .slice(0, 4);
      setArticles(filtered);
      
    } catch (error) {
      console.error('Error fetching gameday data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchGames();
    // Auto-refresh every 60 seconds on gameday
    const interval = setInterval(fetchGames, 60000);
    return () => clearInterval(interval);
  }, [fetchGames]);
  
  // Find game of the week
  const gameOfWeek = games.find(g => g.is_game_of_week);
  const otherGames = games.filter(g => !g.is_game_of_week);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] to-[#0a2463] flex items-center justify-center">
        <div className="text-white text-xl">Loading Gameday Scoreboard...</div>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>SWAC Gameday Scoreboard | SheLovesThee</title>
        <meta name="description" content="Live SWAC football scores, game updates, players to watch, and gameday coverage." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] via-[#0a2463] to-[#071b45]">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-700 py-6 shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-4xl">🏈</span>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                      SWAC GAMEDAY
                    </h1>
                    <p className="text-yellow-300 font-bold">
                      {currentWeek?.title || 'Week 5'} • {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>
              <Link 
                to="/swac-pickem"
                className="bg-yellow-400 hover:bg-yellow-500 text-red-900 font-black px-6 py-3 rounded-lg transition-colors flex items-center gap-2"
              >
                Make Picks <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Game of the Week Feature */}
          {gameOfWeek && (
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                <h2 className="text-2xl font-black text-white uppercase">Game of the Week</h2>
              </div>
              <div className="max-w-xl">
                <GameCard game={gameOfWeek} />
              </div>
            </section>
          )}
          
          {/* All Games Grid */}
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-6 h-6 text-blue-400" />
              <h2 className="text-2xl font-black text-white uppercase">Today's Games</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherGames.map(game => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </section>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Players of the Week - Uses new API-driven component */}
            <section className="lg:col-span-2">
              <SWACWeeklyAwards />
            </section>
            
            {/* Players to Watch - Uses new API-driven component */}
            <section>
              <PlayersToWatch />
            </section>
          </div>
          
          {/* Related Articles */}
          {articles.length > 0 && (
            <section className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-6 h-6 text-blue-400" />
                  <h2 className="text-xl font-black text-white uppercase">Gameday Reading</h2>
                </div>
                <Link to="/news" className="text-blue-400 hover:text-blue-300 text-sm font-bold flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {articles.map(article => (
                  <RelatedArticleCard key={article.article_id} article={article} />
                ))}
              </div>
            </section>
          )}
          
          {/* Quick Links */}
          <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link 
              to="/swac-pickem"
              className="bg-red-600 hover:bg-red-700 text-white font-bold p-4 rounded-xl text-center transition-colors"
            >
              🎯 SWAC Pick'em
            </Link>
            <Link 
              to="/qb-analytics"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold p-4 rounded-xl text-center transition-colors"
            >
              📊 QB Analytics
            </Link>
            <Link 
              to="/swac-scoreboard"
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold p-4 rounded-xl text-center transition-colors"
            >
              📋 Full Scoreboard
            </Link>
            <Link 
              to="/news"
              className="bg-slate-600 hover:bg-slate-700 text-white font-bold p-4 rounded-xl text-center transition-colors"
            >
              📰 All News
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}
